
// Analytics Tracking Utility
// Tracks page views and affiliate link clicks to Supabase

import { supabase } from '../lib/supabase';

export interface AnalyticsEvent {
  id?: string;
  type: 'page_view' | 'affiliate_click';
  timestamp: number;
  visitorId: string;
  device: 'desktop' | 'mobile' | 'tablet';
  meta?: {
    linkType?: string;
    url?: string;
    page?: string;
    sourcePage?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  };
}

const VISITOR_ID_KEY = 'bpml_visitor_id';

// Generate or retrieve persistent visitor ID
function getVisitorId(): string {
  try {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
  } catch {
    return `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Detect device type from user agent
function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent.toLowerCase();
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|opera mobi|skyfire|maemo|windows phone|palm|iemobile|symbian|symbianos|fennec/i.test(ua)) return 'mobile';
  return 'desktop';
}

// Extract UTM parameters from URL
function getUTMParams(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
    };
  } catch {
    return {};
  }
}

// Track a new event — fire and forget to Supabase
export async function trackEvent(type: AnalyticsEvent['type'], meta?: AnalyticsEvent['meta']): Promise<void> {
  try {
    const utmParams = getUTMParams();
    await supabase.from('analytics_events').insert({
      type,
      timestamp: Date.now(),
      visitor_id: getVisitorId(),
      device: getDeviceType(),
      meta: { ...meta, ...utmParams },
    });
  } catch (error) {
    console.error('Failed to track event:', error);
  }
}

export async function trackPageView(page: string = 'home'): Promise<void> {
  await trackEvent('page_view', { page });
}

export async function trackAffiliateClick(linkType: string, url: string, sourcePage?: string): Promise<void> {
  await trackEvent('affiliate_click', { linkType, url, sourcePage });
}

export async function trackOutboundClick(linkType: string, url: string, sourcePage?: string): Promise<void> {
  await trackAffiliateClick(linkType, url, sourcePage);
}

// ── Read helpers (used by dashboard) ────────────────────────────────────────

export interface DBRow {
  type: 'page_view' | 'affiliate_click';
  timestamp: number;
  visitor_id: string;
  device: 'desktop' | 'mobile' | 'tablet';
  meta: Record<string, string | undefined>;
}

export async function getEvents(startTs?: number, endTs?: number): Promise<DBRow[]> {
  let query = supabase
    .from('analytics_events')
    .select('type, timestamp, visitor_id, device, meta')
    .order('timestamp', { ascending: true });

  if (startTs) query = query.gte('timestamp', startTs);
  if (endTs)   query = query.lte('timestamp', endTs);

  const { data, error } = await query;
  if (error) { console.error('getEvents error:', error); return []; }
  return (data ?? []) as DBRow[];
}

export async function clearAllEvents(): Promise<void> {
  const { error } = await supabase
    .from('analytics_events')
    .delete()
    .gte('timestamp', 0);
  if (error) console.error('clearAllEvents error:', error);
}

// ── Derived metrics ──────────────────────────────────────────────────────────

export interface UTMBreakdown {
  campaign: string;
  source: string;
  medium: string;
  visits: number;
  conversions: number;
  rate: number;
}

export interface DeviceBreakdown {
  desktop: { count: number; percentage: number };
  mobile:  { count: number; percentage: number };
  tablet:  { count: number; percentage: number };
}

export interface DailyTrend {
  date: string;
  visitors: number;
  conversions: number;
}

export function computeUniqueVisitors(rows: DBRow[]): number {
  return new Set(rows.map(r => r.visitor_id)).size;
}

export function computeConversionRate(rows: DBRow[]): number {
  const unique = new Set(rows.map(r => r.visitor_id)).size;
  const clicks = rows.filter(r => r.type === 'affiliate_click').length;
  return unique === 0 ? 0 : (clicks / unique) * 100;
}

export function computeUTMBreakdown(rows: DBRow[]): UTMBreakdown[] {
  const map = new Map<string, { visits: Set<string>; conversions: number; source: string; medium: string }>();

  rows.forEach(row => {
    const campaign = row.meta?.utm_campaign || 'Direct / No UTM';
    const source   = row.meta?.utm_source   || '-';
    const medium   = row.meta?.utm_medium   || '-';
    const key = `${campaign}|${source}|${medium}`;

    if (!map.has(key)) map.set(key, { visits: new Set(), conversions: 0, source, medium });
    const d = map.get(key)!;
    d.visits.add(row.visitor_id);
    if (row.type === 'affiliate_click') d.conversions++;
  });

  const result: UTMBreakdown[] = [];
  map.forEach((d, key) => {
    const [campaign] = key.split('|');
    const visits = d.visits.size;
    result.push({ campaign, source: d.source, medium: d.medium, visits, conversions: d.conversions, rate: visits > 0 ? (d.conversions / visits) * 100 : 0 });
  });
  return result.sort((a, b) => b.visits - a.visits);
}

export function computeDeviceBreakdown(rows: DBRow[]): DeviceBreakdown {
  const pageViews = rows.filter(r => r.type === 'page_view');
  const counts = { desktop: 0, mobile: 0, tablet: 0 };
  pageViews.forEach(r => { counts[r.device]++; });
  const total = pageViews.length;
  return {
    desktop: { count: counts.desktop, percentage: total > 0 ? (counts.desktop / total) * 100 : 0 },
    mobile:  { count: counts.mobile,  percentage: total > 0 ? (counts.mobile  / total) * 100 : 0 },
    tablet:  { count: counts.tablet,  percentage: total > 0 ? (counts.tablet  / total) * 100 : 0 },
  };
}

export function computeDailyTrend(rows: DBRow[]): DailyTrend[] {
  const map = new Map<string, { visitors: Set<string>; conversions: number }>();
  rows.forEach(row => {
    const date = new Date(row.timestamp).toISOString().split('T')[0];
    if (!map.has(date)) map.set(date, { visitors: new Set(), conversions: 0 });
    const d = map.get(date)!;
    d.visitors.add(row.visitor_id);
    if (row.type === 'affiliate_click') d.conversions++;
  });
  const trend: DailyTrend[] = [];
  map.forEach((d, date) => trend.push({ date, visitors: d.visitors.size, conversions: d.conversions }));
  return trend.sort((a, b) => a.date.localeCompare(b.date));
}

// Legacy sync shims — kept so existing callers don't break during transition
export function getUniqueVisitorCount(): number { return 0; }
export function getConversionRate(): number { return 0; }
export function getUTMBreakdown(): UTMBreakdown[] { return []; }
export function getDeviceBreakdown(): DeviceBreakdown {
  return {
    desktop: { count: 0, percentage: 0 },
    mobile:  { count: 0, percentage: 0 },
    tablet:  { count: 0, percentage: 0 },
  };
}
export function getDailyTrend(): DailyTrend[] { return []; }
